import { SoffApiExceptionDto } from '@soff/shared-ui';
import {
  DisplayStatus,
  EscalationPattern,
  SoffPageDto,
  TscEmployeeSto,
  TscRegionSto,
  TscSalesOrganizationSto,
  TscWorkshopSto,
} from '@soff/tsc-ui/core';
import { TscAdminActions } from '.';

export interface TscAdminState {
  escalationPatternPage: {
    meta: SoffPageDto | null;
    data: EscalationPattern[] | null;
    error: SoffApiExceptionDto | null;
  } | null;
  escalationPatternPageLoading: boolean;
  displayStatusPage: {
    meta: SoffPageDto | null;
    data: DisplayStatus[] | null;
    error: SoffApiExceptionDto | null;
  } | null;
  displayStatusPageLoading: boolean;
  regionPage: {
    meta: SoffPageDto | null;
    data: TscRegionSto[] | null;
    error: SoffApiExceptionDto | null;
  } | null;
  regionPageLoading: boolean;
  vkorgPage: {
    meta: SoffPageDto | null;
    data: TscSalesOrganizationSto[] | null;
    error: SoffApiExceptionDto | null;
  } | null;
  vkorgPageLoading: boolean;
  workshopPage: {
    meta: SoffPageDto | null;
    data: TscWorkshopSto[] | null;
    error: SoffApiExceptionDto | null;
  } | null;
  workshopPageLoading: boolean;
  workshops: Record<string, TscWorkshopSto>;
  employeePage: {
    meta: SoffPageDto | null;
    data: TscEmployeeSto[] | null;
    error: SoffApiExceptionDto | null;
  } | null;
  employeePageLoading: boolean;
  employees: Record<string, TscEmployeeSto>;
  employeeLoading: boolean;
}

const initialState: TscAdminState = {
  escalationPatternPage: null,
  escalationPatternPageLoading: false,
  displayStatusPage: null,
  displayStatusPageLoading: false,
  regionPage: null,
  regionPageLoading: false,
  vkorgPage: null,
  vkorgPageLoading: false,
  workshopPage: null,
  workshopPageLoading: false,
  workshops: {},
  employeePage: null,
  employeePageLoading: false,
  employees: {},
  employeeLoading: false,
};

export function reducer(state = initialState, action: TscAdminActions.Actions): TscAdminState {
  switch (action.type) {
    case TscAdminActions.GET_ESCALATION_PATTERNS:
      return {
        ...state,
        escalationPatternPageLoading: true,
      };
    case TscAdminActions.GET_ESCALATION_PATTERNS_SUCCESS:
      return {
        ...state,
        escalationPatternPageLoading: false,
        escalationPatternPage: {
          meta: action.response.meta?.pagination,
          data: action.response.data,
          error: null,
        },
      };
    case TscAdminActions.GET_ESCALATION_PATTERNS_FAILURE:
      return {
        ...state,
        escalationPatternPageLoading: false,
        escalationPatternPage: {
          meta: null,
          data: null,
          error: action.error,
        },
      };
    case TscAdminActions.GET_DISPLAY_STATUS:
      return {
        ...state,
        displayStatusPageLoading: true,
      };
    case TscAdminActions.GET_DISPLAY_STATUS_SUCCESS:
      return {
        ...state,
        displayStatusPageLoading: false,
        displayStatusPage: {
          meta: action.response.meta?.pagination,
          data: action.response.data,
          error: null,
        },
      };
    case TscAdminActions.GET_DISPLAY_STATUS_FAILURE:
      return {
        ...state,
        displayStatusPageLoading: false,
        displayStatusPage: {
          meta: null,
          data: null,
          error: action.error,
        },
      };
    case TscAdminActions.UPDATE_DISPLAY_STATUS_SUCCESS:
      return {
        ...state,
        displayStatusPage: {
          ...state.displayStatusPage,
          data: state.displayStatusPage.data.map(status =>
            status.aggregateId === action.payload.aggregateId ? { ...status, ...action.payload } : status
          ),
        },
      };
    case TscAdminActions.GET_REGIONS:
      return {
        ...state,
        regionPageLoading: true,
      };
    case TscAdminActions.GET_REGIONS_SUCCESS:
      return {
        ...state,
        regionPageLoading: false,
        regionPage: {
          meta: action.response.meta?.pagination,
          data: action.response.data.map(dto => new TscRegionSto(dto)),
          error: null,
        },
      };
    case TscAdminActions.GET_REGIONS_FAILURE:
      return {
        ...state,
        regionPageLoading: false,
        regionPage: {
          meta: null,
          data: null,
          error: action.error,
        },
      };
    case TscAdminActions.GET_VKORGS:
      return {
        ...state,
        vkorgPageLoading: true,
      };
    case TscAdminActions.GET_VKORGS_SUCCESS:
      return {
        ...state,
        vkorgPageLoading: false,
        vkorgPage: {
          meta: action.response.meta?.pagination,
          data: action.response.data.map(dto => new TscSalesOrganizationSto(dto)),
          error: null,
        },
      };
    case TscAdminActions.GET_VKORGS_FAILURE:
      return {
        ...state,
        vkorgPageLoading: false,
        vkorgPage: {
          meta: null,
          data: null,
          error: action.error,
        },
      };
    case TscAdminActions.GET_WORKSHOPS:
      return {
        ...state,
        workshopPageLoading: true,
      };
    case TscAdminActions.GET_WORKSHOPS_SUCCESS: {
      const workshopList = action?.response?.data.map(dto => new TscWorkshopSto(dto));
      return {
        ...state,
        workshopPage: {
          meta: action.response.meta?.pagination,
          data: workshopList,
          error: null,
        },
        workshopPageLoading: false,
        workshops: patchWorkshops(state.workshops, workshopList),
      };
    }
    case TscAdminActions.GET_WORKSHOPS_FAILURE:
      return {
        ...state,
        workshopPageLoading: false,
        workshopPage: {
          meta: null,
          data: null,
          error: action.error,
        },
      };
    case TscAdminActions.GET_WORKSHOP_SUCCESS: {
      const workshop = new TscWorkshopSto(action.response.data);
      return {
        ...state,
        workshopPage: state.workshopPage?.data
          ? {
              ...state.workshopPage,
              data: state.workshopPage.data?.map(w => (w.aggregateId !== workshop.aggregateId ? w : workshop)),
            }
          : state.workshopPage,
        workshops: patchWorkshops(state.workshops, [new TscWorkshopSto(action.response.data)]),
      };
    }
    case TscAdminActions.GET_EMPLOYEES:
      return {
        ...state,
        employeePageLoading: true,
      };
    case TscAdminActions.GET_EMPLOYEES_SUCCESS: {
      const employeeList = action?.response?.data.map(employee => new TscEmployeeSto(employee, action.response.related));
      return {
        ...state,
        employeePage: {
          meta: action.response.meta?.pagination,
          data: employeeList,
          error: null,
        },
        employeePageLoading: false,
        employees: patchEmployees(state.employees, employeeList),
      };
    }
    case TscAdminActions.GET_EMPLOYEES_FAILURE:
      return {
        ...state,
        employeePageLoading: false,
        employeePage: {
          meta: null,
          data: null,
          error: action.error,
        },
      };
    case TscAdminActions.GET_EMPLOYEE:
      return {
        ...state,
        employeeLoading: true,
      };
    case TscAdminActions.GET_EMPLOYEE_SUCCESS: {
      const employee = new TscEmployeeSto(action.response.data, action.response.related);
      return {
        ...state,
        employeePage: state.employeePage?.data
          ? {
              ...state.employeePage,
              data: state.employeePage.data?.map(e => (e.aggregateId !== employee.aggregateId ? e : employee)),
            }
          : state.employeePage,
        employees: patchEmployees(state.employees, [employee]),
        employeeLoading: false,
      };
    }
    case TscAdminActions.GET_EMPLOYEE_FAILURE:
      return {
        ...state,
        employeeLoading: false,
      };
    case TscAdminActions.GET_EMPLOYEE_CACHED:
      return {
        ...state,
        employeeLoading: false,
      };
    default:
      return state;
  }
}

function patchWorkshops(workshops: Record<string, TscWorkshopSto>, workshopList: TscWorkshopSto[]): Record<string, TscWorkshopSto> {
  const data = { ...workshops };
  workshopList.forEach(workshop => (data[workshop.aggregateId] = workshop));
  return data;
}

function patchEmployees(employees: Record<string, TscEmployeeSto>, employeeList: TscEmployeeSto[]): Record<string, TscEmployeeSto> {
  const data = { ...employees };
  employeeList.forEach(employee => {
    if (JSON.stringify(employee) !== JSON.stringify(data[employee.aggregateId])) {
      data[employee.aggregateId] = employee;
    }
  });
  return data;
}

export const getEscalationPatternPage = (state: TscAdminState) => state.escalationPatternPage;
export const getEscalationPatternPageLoading = (state: TscAdminState) => state.escalationPatternPageLoading;
export const getDisplayStatusPage = (state: TscAdminState) => state.displayStatusPage;
export const getDisplayStatusPageLoading = (state: TscAdminState) => state.displayStatusPageLoading;
export const getRegionPage = (state: TscAdminState) => state.regionPage;
export const getRegionPageLoading = (state: TscAdminState) => state.regionPageLoading;
export const getVkorgPage = (state: TscAdminState) => state.vkorgPage;
export const getVkorgPageLoading = (state: TscAdminState) => state.vkorgPageLoading;
export const getWorkshops = (state: TscAdminState) => state.workshops;
export const getWorkshopPage = (state: TscAdminState) => state.workshopPage;
export const getWorkshopPageLoading = (state: TscAdminState) => state.workshopPageLoading;
export const getEmployees = (state: TscAdminState) => state.employees;
export const getEmployeePage = (state: TscAdminState) => state.employeePage;
export const getEmployeePageLoading = (state: TscAdminState) => state.employeePageLoading;
export const getEmployeeLoading = (state: TscAdminState) => state.employeeLoading;
